import { useState, useEffect, useRef, useCallback } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FiColumns, FiPlus, FiTrash2, FiCopy } from 'react-icons/fi';
import { v4 as uuidv4 } from 'uuid';

// Sortable Metric Item Component
function SortableMetricItem({ metric, onUpdate, onDelete, onDuplicate }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: metric.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleInputChange = (field, value) => {
    const updatedMetric = { ...metric, [field]: value };
    onUpdate(metric.id, updatedMetric);
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-white border border-gray-200 rounded-lg p-4 mb-3 shadow-sm ${
        isDragging ? 'shadow-lg' : ''
      }`}
    >
      <div className="flex items-start gap-3">
        {/* Drag Handle */}
        <div
          {...attributes}
          {...listeners}
          className="flex items-center justify-center w-6 h-6 text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing mt-2"
        >
          <FiColumns size={16} />
        </div>

        {/* Metric Content */}
        <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Description */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Descrição
            </label>
            <input
              type="text"
              value={metric.description || ''}
              onChange={(e) => handleInputChange('description', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Ex: Taxa de conversão"
            />
          </div>

          {/* Percentage and Color */}
          <div className="flex items-end gap-4 w-full md:col-span-2">
            <div className="flex-1 w-full">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Porcentagem (%)
              </label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={metric.percent || 0}
                onChange={(e) => handleInputChange('percent', parseFloat(e.target.value) || 0)}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Cor
              </label>
              <select
                value={metric.color || 'blue'}
                onChange={e => handleInputChange('color', e.target.value)}
                className="w-50 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="green">Verde</option>
                <option value="red">Vermelho</option>
                <option value="orange">Laranja</option>
                <option value="blue">Azul</option>
              </select>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col gap-2">
          <button
            onClick={() => onDuplicate(metric)}
            className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
            title="Duplicar métrica"
          >
            <FiCopy size={16} />
          </button>
          <button
            onClick={() => onDelete(metric.id)}
            className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
            title="Deletar métrica"
          >
            <FiTrash2 size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ConfigMetrics({ updateBlock, block, setEditingBlock, setBlocks, blocks }) {

  // Initialize default metrics and columns if they don't exist
  const initializeDefaults = () => {
    if (!block.metrics) {
      updateBlock(block.id, 'metrics', [
        { id: 'metric-'+uuidv4(), percent: 75, description: '75% das pessoas tem esse problema', color: 'red' },
        { id: 'metric-'+uuidv4(), percent: 20, description: '20% das pessoas não tem esse problema', color: 'orange' },
        { id: 'metric-'+uuidv4(), percent: 5, description: '5% das pessoas são imunes a esse problema', color: 'green' },
      ]);
    }
    
    if (!block.columns) {
      updateBlock(block.id, 'columns', 2);
    }
  };

  // Initialize defaults on first render
  useEffect(() => {
    initializeDefaults();
  }, []); // Only run once on mount

  const [metrics, setMetrics] = useState(block.metrics || []);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Update local state when block.metrics changes
  useEffect(() => {
    if (block.metrics && block.metrics.length > 0) {
      setMetrics(block.metrics);
    }
  }, [block.metrics]);

  // Update block when metrics change - but prevent initial render update
  const updateBlockMetrics = useCallback((newMetrics) => {
    updateBlock(block.id, 'metrics', newMetrics);
  }, [updateBlock, block.id]);

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setMetrics((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        const newMetrics = arrayMove(items, oldIndex, newIndex);
        updateBlockMetrics(newMetrics);
        return newMetrics;
      });
    }
  };

  const addNewMetric = () => {
    const newMetric = {
      id: 'metric-'+uuidv4(),
      description: 'Nova métrica',
      percent: 0,
      color: 'blue'
    };
    const newMetrics = [...metrics, newMetric];
    setMetrics(newMetrics);
    updateBlockMetrics(newMetrics);
  };

  const updateMetric = (id, updatedMetric) => {
    const newMetrics = metrics.map(metric => 
      metric.id === id ? updatedMetric : metric
    );
    setMetrics(newMetrics);
    updateBlockMetrics(newMetrics);
  };

  const deleteMetric = (id) => {
    if (metrics.length > 1) {
      const newMetrics = metrics.filter(metric => metric.id !== id);
      setMetrics(newMetrics);
      updateBlockMetrics(newMetrics);
    }
  };

  const duplicateMetric = (metricToDuplicate) => {
    const duplicated = {
      ...metricToDuplicate,
      id: 'metric-'+uuidv4(),
      description: `${metricToDuplicate.description} (cópia)`,
    };
    const newMetrics = [...metrics, duplicated];
    setMetrics(newMetrics);
    updateBlockMetrics(newMetrics);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Métricas</h3>
          <p className="text-sm text-gray-600">Configure as métricas do seu bloco</p>
        </div>
        <button
          onClick={addNewMetric}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <FiPlus size={16} />
          Adicionar Métrica
        </button>
      </div>

      {/* Global Columns Setting */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Colunas Globais do Bloco
        </label>
        <select
          value={block.columns || 1}
          onChange={(e) => updateBlock(block.id, 'columns', parseInt(e.target.value, 10))}
          className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value={1}>Lista</option>
          <option value={2}>2 Colunas</option>
          <option value={3}>3 Colunas</option>
          <option value={4}>4 Colunas</option> 
        </select>
        <p className="text-xs text-gray-500 mt-1">
          Define o layout geral do bloco. Cada métrica pode ter sua própria configuração de colunas.
        </p>
      </div>

      {/* Metrics List */}
      <div>
        <h4 className="text-md font-medium text-gray-800 mb-3">
          Métricas ({metrics.length})
        </h4>
        
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={metrics.map(m => m.id)}
            strategy={verticalListSortingStrategy}
          >
            {metrics.map((metric) => (
              <SortableMetricItem
                key={metric.id}
                metric={metric}
                onUpdate={updateMetric}
                onDelete={deleteMetric}
                onDuplicate={duplicateMetric}
              />
            ))}
          </SortableContext>
        </DndContext>
      </div>
 
    </div>
  );
}